import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  signal,
  untracked,
} from '@angular/core';
import { MatProgressBar } from '@angular/material/progress-bar';
import { FileHashPipe } from '../../pipes/file-hash.pipe';
import { Card } from '../card/card.component';

/**
 * Define the UploadFields and the status of the pending uploads
 */
export interface UploadFieldProgress {
  id: string;
  key: string;
  progress: number;
  completed: boolean;
  scanTime: number;
}

@Component({
  selector: 'procure-form-header-uploads',
  templateUrl: './form-header-uploads.component.html',
  styleUrl: './form-header-uploads.component.scss',
  imports: [MatProgressBar, Card, FileHashPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormHeaderUploads {
  readonly uploadFieldsProgress = input.required<UploadFieldProgress[]>();

  readonly scanProgress = signal<Record<string, number>>({});

  constructor() {
    effect(() => {
      const scanning = untracked(() => Object.keys(this.scanProgress()));
      const files = this.uploadFieldsProgress()
        .filter((field) => field.completed && !scanning.includes(field.id))
        .reduce(
          (acc, field) => ({
            ...acc,
            [field.id]: field.scanTime,
          }),
          {} as Record<string, number>,
        );

      this.scanProgress.update((value) => ({
        ...value,
        ...Object.keys(files).reduce(
          (acc, key) => ({
            ...acc,
            [key]: 0,
          }),
          {},
        ),
      }));

      Object.entries(files).forEach(([id, scanTime]) => {
        this.scan(id, scanTime);
      });
    });
  }

  private scan(id: string, scanTime: number): void {
    const interval = scanTime / 50;
    let progress = 0;

    const intervalId = setInterval(() => {
      progress += 2;

      this.scanProgress.update((value) => ({
        ...value,
        [id]: progress,
      }));

      if (progress >= 100) {
        clearInterval(intervalId);
      }
    }, interval);
  }
}
